import { useTheme } from "@mui/material";
import * as React from "react";

import { RobotoButton } from "@/shared/components/RobotoButton";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { AddIcon } from "@/shared/foundation/icons/mui";
import {
  actions,
  PlotPanelState,
  useVizDispatch,
} from "@/shared/state/visualization";

import { DataSourceSelector } from "../../controls/DataSourceSelector";
import { isViewableAsPlot } from "../../Panel/predicates";
import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";

import styles from "./AddSeries.module.css";
import { type PlotPanelSettingsSidebarProps } from "./PlotPanelSettings";

interface AddSeriesProps {
  plotPanel: PlotPanelState;
}

export function AddSeries({ plotPanel }: AddSeriesProps) {
  const vizStateDispatch = useVizDispatch();
  const sidebar = useWorkspaceSidebar();
  const [isAddingNewSeries, setIsAddingNewSeries] = React.useState(() => {
    return plotPanel.data.length === 0;
  });

  const theme = useTheme();

  if (isAddingNewSeries) {
    return (
      <>
        <RobotoTypography component={"h3"} className={styles.title}>
          New series
        </RobotoTypography>
        <DataSourceSelector
          onChange={(messagePathNode) => {
            const seriesId = crypto.randomUUID();
            vizStateDispatch(
              actions.addSeriesToPlotPanel(
                plotPanel.id,
                messagePathNode.toTopicData(),
                seriesId,
              ),
            );
            // Spotlight the new series so its row opens expanded and scrolled
            // into view — the same view data a legend gear click supplies.
            // When the reducer skips the add (its data source is already
            // plotted), the id matches no row and nothing expands.
            sidebar.openView<PlotPanelSettingsSidebarProps>({
              type: SidebarViewType.PanelSettings,
              data: {
                field: "Series",
                seriesId,
                spotlightKey: performance.now(),
              },
            });
            setIsAddingNewSeries(false);
          }}
          optionFilter={isViewableAsPlot}
        />
        <div
          className={styles.positionRight}
          style={{ marginTop: theme.spacing(0.5) }}
        >
          <RobotoButton
            eventName={"AddSeriesCanceled"}
            eventProperties={{
              panelId: plotPanel.id,
            }}
            onClick={() => setIsAddingNewSeries(false)}
            size="small"
          >
            Cancel
          </RobotoButton>
        </div>
      </>
    );
  }

  return (
    <RobotoButton
      eventName={"AddSeriesClicked"}
      eventProperties={{
        panelId: plotPanel.id,
      }}
      startIcon={<AddIcon />}
      onClick={() => setIsAddingNewSeries(true)}
      size="small"
    >
      Add Series
    </RobotoButton>
  );
}
